import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Edit, Trash2, Search, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/shared/ui/alert-dialog";
import { Pagination, DataTable } from "@/shared/widgets";
import { useDebounce } from "@/shared/hooks/useDebounce";

import {
  useReceivableShareConfigs,
  useDeleteReceivableShareConfig,
} from "../hooks/share-config.hooks";
import { ReceivableShareConfig } from "../utils/types";

const DEFAULT_PAGE_SIZE = 10;

export function ReceivableShareConfigList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const debouncedSearch = useDebounce(search, 400);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page") ?? "1") || 1
  );
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE
  );
  const [shareConfigToDelete, setShareConfigToDelete] = useState<ReceivableShareConfig | null>(null);

  const { shareConfigs, totalPages, totalItems, currentPage: apiPage, isLoading, isFetching, error } =
    useReceivableShareConfigs({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch || undefined,
      sortBy: "createdAt",
      order: "desc",
    });

  const { deleteShareConfig, isLoading: isDeleting } = useDeleteReceivableShareConfig();

  // Atualiza query params quando filtros/paginação mudam
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (currentPage > 1) params.set("page", String(currentPage));
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(pageSize));
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, currentPage, pageSize, setSearchParams]);

  // Reseta página ao alterar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, pageSize]);

  // Mantém paginação sincronizada com API
  useEffect(() => {
    if (apiPage && apiPage !== currentPage) {
      setCurrentPage(apiPage);
    }
  }, [apiPage, currentPage]);

  const handleEdit = (id: string) => {
    navigate(`/receivable-share-configs/${id}`);
  };

  const handleDelete = async () => {
    if (!shareConfigToDelete) return;
    try {
      await deleteShareConfig(shareConfigToDelete.id);
      setShareConfigToDelete(null);
    } catch (error) {
      console.error("Erro ao remover configuração:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações de Rateio</h1>
          <p className="text-muted-foreground">
            Cadastre pesos e parâmetros utilizados para calcular o rateio de recebíveis.
          </p>
        </div>
        <Button onClick={() => navigate("/receivable-share-configs/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Nova configuração
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Pesquise por nome ou descrição dos rateios configurados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={shareConfigs}
        isLoading={isLoading}
        error={error}
        emptyTitle="Nenhuma configuração encontrada"
        emptyDescription={(hasFilters) =>
          hasFilters
            ? "Tente ajustar os filtros"
            : "Comece criando uma nova configuração"
        }
        emptyActionLabel="Nova configuração"
        emptyActionOnClick={() => navigate("/receivable-share-configs/new")}
        hasFilters={Boolean(search)}
        errorMessage={`Erro ao carregar configurações: ${error?.message}`}
        columns={[
          {
            header: "Nome",
            cell: (config) => (
                          <span className="font-medium">{config.name}</span>
            ),
            className: "align-top",
          },
          {
            header: "Descrição",
            cell: (config) => (
              <span className="text-sm text-muted-foreground">
                          {config.description ? (
                            <span className="line-clamp-3">{config.description}</span>
                          ) : (
                            <span className="italic text-muted-foreground/70">Sem descrição</span>
                          )}
              </span>
            ),
            className: "align-top text-sm text-muted-foreground",
          },
          {
            header: "Última atualização",
            cell: (config) => {
              const lastUpdatedAt = config.lastUpdatedAt ?? config.updatedAt ?? null;
              return (
                <span className="text-sm text-muted-foreground">
                          {lastUpdatedAt ? formatDateValue(lastUpdatedAt) : "—"}
                </span>
              );
            },
            className: "align-top text-sm text-muted-foreground w-48",
          },
          {
            header: "Ações",
            cell: (config) => (
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(config.id)} title="Editar">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setShareConfigToDelete(config)}
                  title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
            ),
            className: "align-top text-right w-32",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.max(totalPages, 1)}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(size) => {
            setCurrentPage(1);
              setPageSize(size);
            }}
            totalItems={totalItems}
            showTotalItems
          syncWithQueryParams={false}
          />
      )}

      <AlertDialog open={Boolean(shareConfigToDelete)} onOpenChange={(open) => !open && setShareConfigToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover configuração?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação é irreversível. A configuração de rateio será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removendo...
                </>
              ) : (
                "Remover"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const formatDateValue = (value: string) =>
  format(new Date(value), "dd/MM/yyyy HH:mm", {
    locale: ptBR,
  });
